import type {
  ActivateVersionResult,
  AgentDelegationStatsResult,
  AgentDraftInput,
  AgentDraftResult,
  AgentDto,
  AgentInput,
  AgentPatch,
  AgentVersionDto,
  AgentVersionInput,
  AgentWorkspacesResult,
  ApiTokenCreatedDto,
  ApiTokenDto,
  ApiTokenInput,
  ApprovalDto,
  ArtifactPreviewResult,
  AttachmentDto,
  AuditEntryDto,
  AuditFacetsDto,
  BillingCheckoutInput,
  BillingDto,
  BillingPlansDto,
  BudgetDto,
  BudgetInput,
  BulkAgentPermissionsInput,
  ChannelDto,
  ChannelPostDto,
  ChannelSearchHitDto,
  ChannelTestResult,
  ClaudeLoginDto,
  CodexLoginResult,
  CompanyDeleteInput,
  CompanyDto,
  CompanyFallbackPolicy,
  CompanyFallbackPolicyPatch,
  CompanyInput,
  CompanyPatch,
  ConversationDto,
  DeliveryDto,
  EgressRequestDto,
  FlowAttachInput,
  FlowAttachResult,
  FlowDto,
  FlowInput,
  FlowPatch,
  FlowRunInput,
  FlowRunResult,
  FlowSuggestionsDto,
  FlowVersionDto,
  FlowVersionInput,
  HaltResult,
  HealthDto,
  ImproveInstructionsInput,
  ImproveInstructionsResult,
  McpDiscoverResult,
  McpOAuthStartInput,
  McpOAuthStartResult,
  McpReposResult,
  McpServerDto,
  McpServerInput,
  McpServerPatch,
  MeDto,
  MemberDto,
  MessageDto,
  ModelDto,
  ModelPatch,
  ModelPrice,
  MySessionDto,
  NotificationDto,
  NotifyChannelDto,
  NotifyChannelInput,
  NotifyChannelPatch,
  NotifyRuleDto,
  NotifyRuleInput,
  OnboardingStatusDto,
  OrgStatusDto,
  PermissionPreview,
  PermissionPreviewInput,
  ProviderDto,
  ProviderInput,
  ProviderPatch,
  ProviderTestResult,
  QuestionDto,
  RecordingDto,
  RecordingEventDto,
  RecordingIngestResult,
  RecordingInput,
  RunAnalysisReport,
  RunAnalysisReportSummary,
  RunAnalysisSettings,
  RunAnalysisSettingsPatch,
  RunContextDto,
  RunDto,
  RunEventDto,
  RunSettingsOverride,
  SetupStatusDto,
  SkillAcceptInput,
  SkillAttachInput,
  SkillDto,
  SkillExportDto,
  SkillImportInput,
  SkillInput,
  SkillReviewInput,
  SkillSuggestionDto,
  SkillUsageDto,
  SkillVersionDto,
  SyncModelsResult,
  TaskDto,
  TaskInput,
  TelegramStatusDto,
  ToolPolicyDto,
  ToolPolicyRule,
  TriggerDto,
  TriggerFireDto,
  TriggerFireResult,
  TriggerInput,
  TriggerPatch,
  UsageSummary,
  WorkspaceActionInput,
} from '@af/shared';
import { ROUTES } from '@af/shared';
import type { z } from 'zod';
import { activeOrg, orgHeaders } from './active-org';
import { RESPONSE_SCHEMAS } from './contracts';

export type { RouteKey } from '@af/shared';

type RouteKey = keyof typeof ROUTES;
type RoutePath<K extends RouteKey> = (typeof ROUTES)[K]['path'];

/** Extract `:param` names from a path literal. */
export type PathParamNames<P extends string> = P extends `${string}:${infer Param}/${infer Rest}`
  ? Param | PathParamNames<`/${Rest}`>
  : P extends `${string}:${infer Param}`
    ? Param
    : never;

type ParamsFor<K extends RouteKey> = [PathParamNames<RoutePath<K>>] extends [never]
  ? { params?: undefined }
  : { params: Record<PathParamNames<RoutePath<K>>, string> };

type Query = Record<string, string | number | boolean | null | undefined>;

/** Request bodies per mutating route. */
export interface BodyMap {
  previewAgentPermissions: PermissionPreviewInput;
  bulkAgentPermissions: BulkAgentPermissionsInput;
  billingCheckout: BillingCheckoutInput;
  updateRunAnalysisSettings: RunAnalysisSettingsPatch;
  updateCompanyFallbackPolicy: CompanyFallbackPolicyPatch;
  orgHalt: { reason?: string };
  orgResume: Record<string, never>;
  createCompany: z.input<typeof CompanyInput>;
  updateCompany: z.input<typeof CompanyPatch>;
  deleteCompany: CompanyDeleteInput;
  agentWorkspaceAction: WorkspaceActionInput;
  createProvider: z.input<typeof ProviderInput>;
  updateProvider: ProviderPatch;
  testProvider: Record<string, never>;
  syncModels: Record<string, never>;
  startCodexLogin: Record<string, never>;
  pollCodexLogin: Record<string, never>;
  disconnectCodex: Record<string, never>;
  startClaudeLogin: Record<string, never>;
  submitClaudeLoginCode: { code: string };
  setAgentLoginOwner: { mine: boolean };
  joinChatgptWaitlist: Record<string, never>;
  updateModel: z.input<typeof ModelPatch>;
  overridePrice: ModelPrice;
  createAgent: z.input<typeof AgentInput>;
  draftAgent: z.input<typeof AgentDraftInput>;
  improveInstructions: z.input<typeof ImproveInstructionsInput>;
  updateAgent: z.input<typeof AgentPatch>;
  createAgentVersion: z.input<typeof AgentVersionInput>;
  activateAgentVersion: Record<string, never>;
  createConversation: { agentId: string; title?: string };
  sendMessage: { text: string; attachmentIds?: string[]; runSettings?: RunSettingsOverride };
  uploadAttachment: { name: string; mediaType: string; data: string };
  setRunAutoConsent: { enabled: boolean; confirmation?: 'run-auto-v1' };
  cancelRun: Record<string, never>;
  continueRun: Record<string, never>;
  restartRun: Record<string, never>;
  createTask: z.input<typeof TaskInput>;
  cancelTask: Record<string, never>;
  decideApproval: {
    approved: boolean;
    reason?: string;
    remember?: 'once' | 'always';
    planDecision?: 'approve' | 'request_changes' | 'reject';
  };
  decideEgressRequest: { approved: boolean; reason?: string };
  upsertBudget: z.input<typeof BudgetInput>;
  markNotificationRead: Record<string, never>;
  telegramLinkCode: Record<string, never>;
  savePushSubscription: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string };
  deletePushSubscription: { endpoint: string };
  answerRun: { answer: string };
  createTrigger: z.input<typeof TriggerInput>;
  updateTrigger: TriggerPatch;
  fireTrigger: { event?: Record<string, unknown>; payload?: unknown };
  createMcpServer: z.input<typeof McpServerInput>;
  updateMcpServer: McpServerPatch;
  discoverMcpServer: Record<string, never>;
  listMcpRepos: Record<string, never>;
  startMcpOAuth: z.input<typeof McpOAuthStartInput>;
  replaceToolPolicies: { rules: z.input<typeof ToolPolicyRule>[] };
  createNotifyChannel: NotifyChannelInput;
  updateNotifyChannel: NotifyChannelPatch;
  deleteNotifyChannel: Record<string, never>;
  sendChannelCode: Record<string, never>;
  verifyNotifyChannel: { code: string };
  testNotifyChannel: Record<string, never>;
  replaceNotifyRules: { rules: NotifyRuleInput[] };
  telegramRegisterWebhook: Record<string, never>;
  createApiToken: z.input<typeof ApiTokenInput>;
  revokeApiToken: Record<string, never>;
  revokeMySession: Record<string, never>;
  createChannel: { slug: string; name: string; topic?: string };
  updateChannel: { name?: string; topic?: string; archived?: boolean };
  setChannelMembers: { members: { agentId: string; inContext: boolean }[] };
  createChannelPost: { text: string; replyToId?: string | null };
  markChannelRead: Record<string, never>;
  setConversationRead: { read: boolean };
  createRecording: z.input<typeof RecordingInput>;
  stopRecording: Record<string, never>;
  synthesizeRecording: Record<string, never>;
  deleteRecording: Record<string, never>;
  ingestRecordingEvents: { events: unknown[] };
  createFlow: z.input<typeof FlowInput>;
  updateFlow: z.input<typeof FlowPatch>;
  deleteFlow: Record<string, never>;
  createFlowVersion: z.input<typeof FlowVersionInput>;
  attachFlow: z.input<typeof FlowAttachInput>;
  runFlow: z.input<typeof FlowRunInput>;
  createSkill: z.input<typeof SkillInput>;
  importSkill: z.input<typeof SkillImportInput>;
  createSkillVersion: z.input<typeof SkillInput>;
  activateSkill: z.input<typeof SkillReviewInput>;
  rejectSkill: z.input<typeof SkillReviewInput>;
  acceptSkill: z.input<typeof SkillAcceptInput>;
  attachSkill: z.input<typeof SkillAttachInput>;
  deleteSkill: Record<string, never>;
}

type Ok = { ok: true };
/** Response types per route (lists normalized from `Paginated<T>` to `T[]`). */
export interface ResponseMap {
  companyFallbackPolicy: CompanyFallbackPolicy;
  updateCompanyFallbackPolicy: CompanyFallbackPolicy;
  health: z.infer<typeof HealthDto>;
  setupStatus: SetupStatusDto;
  billingStatus: BillingDto;
  billingPlans: BillingPlansDto;
  billingCheckout: { url: string };
  billingPortal: { url: string };
  me: MeDto;
  orgStatus: OrgStatusDto;
  onboardingStatus: OnboardingStatusDto;
  orgHalt: z.infer<typeof HaltResult>;
  orgResume: z.infer<typeof HaltResult>;
  listCompanies: CompanyDto[];
  createCompany: Pick<CompanyDto, 'id' | 'name' | 'slug' | 'role'>;
  updateCompany: Pick<CompanyDto, 'id' | 'name' | 'slug'>;
  deleteCompany: Ok;
  listProviders: ProviderDto[];
  createProvider: ProviderDto;
  updateProvider: ProviderDto;
  deleteProvider: Ok;
  testProvider: ProviderTestResult;
  syncModels: z.infer<typeof SyncModelsResult>;
  startCodexLogin: CodexLoginResult;
  pollCodexLogin: CodexLoginResult;
  disconnectCodex: ProviderDto;
  getClaudeLogin: {
    available: boolean;
    login: ClaudeLoginDto | null;
    agents: { id: string; name: string; chosen: boolean }[];
  };
  setAgentLoginOwner: Ok;
  joinChatgptWaitlist: Ok;
  startClaudeLogin: { login: ClaudeLoginDto };
  submitClaudeLoginCode: Ok;
  disconnectClaudeLogin: Ok;
  listModels: ModelDto[];
  updateModel: ModelDto;
  overridePrice: ModelDto;
  listAgents: AgentDto[];
  getAgent: AgentDto;
  previewAgentPermissions: PermissionPreview;
  bulkAgentPermissions: { items: { id: string; versionId: string }[] };
  createAgent: AgentDto;
  draftAgent: AgentDraftResult;
  improveInstructions: ImproveInstructionsResult;
  updateAgent: AgentDto;
  deleteAgent: Ok;
  listAgentVersions: AgentVersionDto[];
  createAgentVersion: AgentVersionDto;
  listAgentWorkspaces: AgentWorkspacesResult;
  agentDelegationStats: AgentDelegationStatsResult;
  agentWorkspaceAction: Ok;
  activateAgentVersion: z.infer<typeof ActivateVersionResult>;
  listConversations: ConversationDto[];
  createConversation: ConversationDto;
  getMessages: MessageDto[];
  getChatFallback: { first: { runId: string; seq: number } | null };
  sendMessage: { messageId: string; runId: string };
  uploadAttachment: AttachmentDto;
  artifactPreview: ArtifactPreviewResult;
  listRuns: RunDto[];
  getRun: RunDto;
  runEvents: RunEventDto[];
  runStream: never;
  setRunAutoConsent: RunDto;
  cancelRun: RunDto;
  continueRun: RunDto;
  restartRun: RunDto;
  listTasks: TaskDto[];
  createTask: TaskDto;
  cancelTask: Ok;
  listApprovals: ApprovalDto[];
  listQuestions: QuestionDto[];
  listEgressRequests: EgressRequestDto[];
  decideEgressRequest: EgressRequestDto;
  decideApproval: ApprovalDto;
  runAnalysisSettings: RunAnalysisSettings;
  updateRunAnalysisSettings: RunAnalysisSettings;
  listRunAnalysisReports: { items: RunAnalysisReportSummary[]; nextCursor: string | null };
  getRunAnalysisReport: RunAnalysisReport;
  runRunAnalysis: RunAnalysisReport;
  usage: UsageSummary;
  listBudgets: BudgetDto[];
  upsertBudget: BudgetDto;
  listNotifications: NotificationDto[];
  markNotificationRead: Ok;
  events: never;
  auditLog: AuditEntryDto[];
  telegramLinkCode: { code: string; expiresAt: string; deepLink: string | null };
  savePushSubscription: Ok;
  deletePushSubscription: Ok;
  vapidPublicKey: { publicKey: string | null };
  telegramWebhook: never;
  listApiTokens: ApiTokenDto[];
  createApiToken: ApiTokenCreatedDto;
  revokeApiToken: Ok;
  listMySessions: MySessionDto[];
  listChannels: ChannelDto[];
  createChannel: ChannelDto;
  updateChannel: ChannelDto;
  setChannelMembers: ChannelDto;
  listChannelPosts: ChannelPostDto[];
  createChannelPost: ChannelPostDto;
  markChannelRead: Ok;
  setConversationRead: Ok;
  searchChannelPosts: ChannelSearchHitDto[];
  revokeMySession: Ok;
  mcpEndpoint: never;
  mcpEndpointGet: never;
  answerRun: RunDto;
  listTriggers: TriggerDto[];
  getTrigger: TriggerDto;
  createTrigger: TriggerDto;
  updateTrigger: TriggerDto;
  deleteTrigger: Ok;
  fireTrigger: TriggerFireResult;
  listTriggerFires: TriggerFireDto[];
  listMcpServers: McpServerDto[];
  createMcpServer: McpServerDto;
  updateMcpServer: McpServerDto;
  deleteMcpServer: Ok;
  discoverMcpServer: McpDiscoverResult;
  listMcpRepos: McpReposResult;
  startMcpOAuth: McpOAuthStartResult;
  mcpOAuthCallback: never;
  listToolPolicies: ToolPolicyDto[];
  replaceToolPolicies: ToolPolicyDto[];
  triggerWebhook: never;
  auditFacets: AuditFacetsDto;
  listMembers: MemberDto[];
  /** Raw (not list-normalized): carries server capability flags next to the items. */
  listNotifyChannels: { items: NotifyChannelDto[]; email: boolean; webpush: boolean };
  createNotifyChannel: NotifyChannelDto;
  updateNotifyChannel: NotifyChannelDto;
  deleteNotifyChannel: Ok;
  sendChannelCode: { sent: true; expiresAt: string };
  verifyNotifyChannel: NotifyChannelDto;
  testNotifyChannel: ChannelTestResult & { queued: boolean };
  listNotifyRules: NotifyRuleDto[];
  replaceNotifyRules: NotifyRuleDto[];
  listDeliveries: DeliveryDto[];
  telegramStatus: TelegramStatusDto;
  telegramRegisterWebhook: { ok: boolean; url: string };
  listRecordings: RecordingDto[];
  createRecording: RecordingDto;
  getRecording: RecordingDto;
  listRecordingEvents: RecordingEventDto[];
  ingestRecordingEvents: RecordingIngestResult;
  stopRecording: RecordingDto;
  synthesizeRecording: RecordingDto;
  deleteRecording: Ok;
  listFlows: FlowDto[];
  getFlow: FlowDto;
  createFlow: FlowDto;
  updateFlow: FlowDto;
  deleteFlow: Ok;
  listFlowVersions: FlowVersionDto[];
  createFlowVersion: FlowDto;
  suggestFlowAgents: FlowSuggestionsDto;
  attachFlow: FlowAttachResult;
  runFlow: FlowRunResult;
  getRunContext: RunContextDto;
  listSkills: SkillDto[];
  getSkill: SkillDto;
  createSkill: SkillDto;
  importSkill: SkillDto;
  exportSkill: SkillExportDto;
  listSkillVersions: SkillVersionDto[];
  createSkillVersion: SkillDto;
  activateSkill: SkillDto;
  rejectSkill: SkillDto;
  listSkillSuggestions: SkillSuggestionDto[];
  listSkillUsage: SkillUsageDto[];
  acceptSkill: SkillDto;
  attachSkill: { agentId: string; version: number };
  deleteSkill: Ok;
}

export type Resp<K extends RouteKey> = K extends keyof ResponseMap ? ResponseMap[K] : unknown;
/** Routes the web client has no response type for yet (they resolve to `unknown`). */
export type UntypedRoutes = Exclude<RouteKey, keyof ResponseMap>;

export type CallOptions<K extends RouteKey> = ParamsFor<K> & {
  query?: Query;
  body?: K extends keyof BodyMap ? BodyMap[K] : undefined;
  signal?: AbortSignal;
};

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export function buildPath(path: string, params?: Record<string, string>, query?: Query): string {
  const p = path.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const v = params?.[name];
    if (v == null) throw new Error(`Missing path param :${name} for ${path}`);
    return encodeURIComponent(v);
  });
  if (!query) return p;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v != null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${p}?${s}` : p;
}

/** Normalizes `T[] | Paginated<T>` → `T[]`. */
export function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

/** Routes whose response is `Paginated<T>`; normalized to `T[]`. */
export const LIST_ROUTES: ReadonlySet<string> = new Set([
  'listApiTokens',
  'listChannels',
  'listChannelPosts',
  'searchChannelPosts',
  'listCompanies',
  'listProviders',
  'listModels',
  'listAgents',
  'listAgentVersions',
  'listConversations',
  'getMessages',
  'listRuns',
  'runEvents',
  'listTasks',
  'listApprovals',
  'listQuestions',
  'listEgressRequests',
  'listBudgets',
  'listNotifications',
  'auditLog',
  'listTriggers',
  'listTriggerFires',
  'listMcpServers',
  'listToolPolicies',
  'replaceToolPolicies',
  'listMembers',
  'listNotifyRules',
  'replaceNotifyRules',
  'listDeliveries',
  'listRecordings',
  'listRecordingEvents',
  'listFlows',
  'listFlowVersions',
  'listSkills',
  'listSkillVersions',
  'listSkillSuggestions',
  'listSkillUsage',
]);

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

/** This tab's company is no longer accessible: drop the pin and reload into the default company. */
function onOrgLost() {
  activeOrg.clear();
  if (typeof location !== 'undefined' && import.meta.env.MODE !== 'test') location.assign('/');
}

export async function call<K extends RouteKey>(key: K, opts?: CallOptions<K>): Promise<Resp<K>> {
  const route = ROUTES[key];
  const url = buildPath(route.path, opts?.params as Record<string, string> | undefined, opts?.query);
  const hasBody = route.method !== 'GET' && (route.method !== 'DELETE' || opts?.body !== undefined);
  const res = await fetch(url, {
    method: route.method,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...orgHeaders(),
      ...(hasBody ? { 'content-type': 'application/json' } : {}),
    },
    body: hasBody ? JSON.stringify(opts?.body ?? {}) : undefined,
    signal: opts?.signal,
  });
  if (res.status === 204) return { ok: true } as Resp<K>;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    if (res.status === 401 && key !== 'me') onUnauthorized?.();
    if (err?.code === 'not_a_member') onOrgLost();
    throw new ApiClientError(
      res.status,
      err?.code ?? `http_${res.status}`,
      err?.message || res.statusText || `Request failed (${res.status})`,
    );
  }
  const schema = (RESPONSE_SCHEMAS as Record<string, z.ZodType | undefined>)[key];
  if (import.meta.env.DEV && schema) {
    const parsed = schema.safeParse(data);
    if (!parsed.success) console.warn(`[api] ${key} response does not match contract`, parsed.error.issues);
  }
  if (LIST_ROUTES.has(key)) return asList(data) as Resp<K>;
  return (data ?? { ok: true }) as Resp<K>;
}

export const api = { call, buildPath };

/**
 * Untyped JSON call for endpoints not (yet) in ROUTES (e.g. notification channel routes).
 * Same error semantics as `call`.
 */
export async function rawCall<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...orgHeaders(),
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiClientError(
      res.status,
      err?.code ?? `http_${res.status}`,
      err?.message || res.statusText || `Request failed (${res.status})`,
    );
  }
  return data as T;
}
